<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Offering Options</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Manage offering options and translations</p>
                                </div>
                                <button
                                        type="button"
                                        class="inline-flex items-center gap-2 bg-orange-7 hover:bg-orange-6 text-white py-2.5 px-4 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors"
                                        @click="showCreateModal = true"
                                >
                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                        </svg>
                                        Create New
                                </button>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="p-6">
                        <!-- Search Card -->
                        <div class="bg-white rounded-xl shadow-sm py-4 px-6 mb-4">
                                <div class="flex flex-wrap gap-4 items-center">
                                        <div class="flex-1 min-w-[12rem]">
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search offering options..."
                                                                class="w-full py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm outline-none"
                                                        >
                                                </div>
                                        </div>
                                        <select
                                                v-model="tagFilter"
                                                class="py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none bg-white min-w-[8rem]"
                                        >
                                                <option value="all">All Tags</option>
                                                <option value="__empty__">No Tag</option>
                                                <option v-for="tag in uniqueTags" :key="tag" :value="tag">
                                                        {{ tag }}
                                                </option>
                                        </select>
                                        <select
                                                v-model="translationFilter"
                                                class="py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none bg-white min-w-[10rem]"
                                        >
                                                <option value="all">All Translations</option>
                                                <option value="complete">Complete</option>
                                                <option value="partial">Partial</option>
                                                <option value="none">None</option>
                                        </select>
                                        <div class="flex items-center gap-2">
                                                <span class="text-xs bg-orange-2 text-orange-7 py-1 px-3 rounded-full font-medium">
                                                        {{ totalRows }} options
                                                </span>
                                        </div>
                                </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading offering options...</span>
                        </div>

                        <!-- Table Card -->
                        <div v-else class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div class="overflow-x-auto">
                                        <table class="w-full border-collapse">
                                                <thead>
                                                        <tr class="bg-gray-50">
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">ID</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Name</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Tag</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Description</th>
                                                                <th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Translations</th>
                                                                <th class="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Actions</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        <tr v-if="filteredItems.length === 0">
                                                                <td colspan="6" class="text-center py-12 px-4">
                                                                        <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                                                        </svg>
                                                                        <p class="text-gray-500 text-sm m-0">No offering options found</p>
                                                                        <p class="text-gray-400 text-xs mt-1 mb-0">Try adjusting your search or create a new one</p>
                                                                </td>
                                                        </tr>
                                                        <template v-for="item in paginatedItems" :key="item.id">
                                                                <tr
                                                                        class="border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                                                                        @click="editItem(item.id)"
                                                                >
                                                                        <td class="py-3 px-4 text-sm text-gray-500 font-mono">{{ item.id }}</td>
                                                                        <td class="py-3 px-4 text-sm font-medium text-gray-900">{{ item.name }}</td>
                                                                        <td class="py-3 px-4 text-sm text-gray-500">{{ item.tag || '-' }}</td>
                                                                        <td class="py-3 px-4 text-sm text-gray-500">{{ item.description || '-' }}</td>
                                                                        <td class="py-3 px-4 text-center" @click.stop>
                                                                                <div class="flex items-center justify-center gap-2">
                                                                                        <span
                                                                                                class="inline-flex items-center py-1 px-2 text-xs font-medium rounded-full"
                                                                                                :class="getTranslationStatusClass(item.id)"
                                                                                        >
                                                                                                {{ getTranslationStatusLabel(item.id) }}
                                                                                        </span>
                                                                                        <button
                                                                                                type="button"
                                                                                                class="p-1 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-orange-7 hover:bg-orange-1"
                                                                                                title="Expand translations"
                                                                                                @click.stop="toggleTranslationExpand(item.id)"
                                                                                        >
                                                                                                <svg 
                                                                                                        class="w-4 h-4 transition-transform"
                                                                                                        :class="expandedRowIds.has(item.id) ? 'rotate-180' : ''"
                                                                                                        fill="none" 
                                                                                                        viewBox="0 0 24 24" 
                                                                                                        stroke="currentColor" 
                                                                                                        stroke-width="2"
                                                                                                >
                                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                                                                                                </svg>
                                                                                        </button>
                                                                                </div>
                                                                        </td>
                                                                        <td class="py-3 px-4 text-right" @click.stop>
                                                                                <div class="flex justify-end gap-2">
                                                                                        <button
                                                                                                type="button"
                                                                                                class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-orange-7"
                                                                                                title="Edit"
                                                                                                @click="editItem(item.id)"
                                                                                        >
                                                                                                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                                </svg>
                                                                                        </button>
                                                                                        <button
                                                                                                type="button"
                                                                                                class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-red-600"
                                                                                                title="Delete"
                                                                                                @click="confirmDelete(item)"
                                                                                        >
                                                                                                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                                </svg>
                                                                                        </button>
                                                                                </div>
                                                                        </td>
                                                                </tr>
                                                                <!-- Expanded Translation Row -->
                                                                <tr v-if="expandedRowIds.has(item.id)" class="bg-gray-50">
                                                                        <td colspan="6" class="p-0">
                                                                                <div class="p-4 border-t border-gray-200">
                                                                                        <!-- Language Tabs -->
                                                                                        <div class="flex border-b border-gray-200 mb-4 overflow-x-auto">
                                                                                                <button
                                                                                                        v-for="lang in languages"
                                                                                                        :key="lang.id"
                                                                                                        type="button"
                                                                                                        class="py-2 px-4 text-sm font-medium whitespace-nowrap border-none bg-transparent cursor-pointer border-b-2 -mb-px"
                                                                                                        :class="selectedLanguageId === lang.id ? 'text-orange-7 border-orange-7' : 'text-gray-500 border-transparent hover:text-gray-700'"
                                                                                                        @click="selectedLanguageId = lang.id"
                                                                                                >
                                                                                                        {{ lang.name }}
                                                                                                        <span
                                                                                                                v-if="getLanguageTranslationStatus(item.id, lang.id) === 'complete'"
                                                                                                                class="ml-1 w-2 h-2 rounded-full bg-green-500 inline-block"
                                                                                                        ></span>
                                                                                                        <span
                                                                                                                v-else
                                                                                                                class="ml-1 w-2 h-2 rounded-full bg-red-500 inline-block"
                                                                                                        ></span>
                                                                                                </button>
                                                                                        </div>
                                                                                        
                                                                                        <!-- Translation Field -->
                                                                                        <div class="max-w-xl">
                                                                                                <label class="block text-sm font-medium text-gray-700 mb-2">Header</label>
                                                                                                <input
                                                                                                        :value="getTranslation(item.id, selectedLanguageId)"
                                                                                                        type="text"
                                                                                                        placeholder="Enter header translation..."
                                                                                                        class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none transition-colors focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                                                        @input="updateTranslation(item.id, selectedLanguageId, ($event.target as HTMLInputElement).value)"
                                                                                                >
                                                                                        </div>
                                                                                        
                                                                                        <!-- Save Button -->
                                                                                        <div class="mt-4 flex items-center gap-3">
                                                                                                <button
                                                                                                        type="button"
                                                                                                        class="inline-flex items-center gap-2 py-2 px-4 bg-orange-7 text-white rounded-lg text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-70"
                                                                                                        :disabled="!hasUnsavedChanges(item.id)"
                                                                                                        @click="saveTranslations(item.id)"
                                                                                                >
                                                                                                        Save Changes
                                                                                                </button>
                                                                                                <span v-if="hasUnsavedChanges(item.id)" class="text-sm text-orange-7 font-medium">
                                                                                                        Unsaved changes
                                                                                                </span>
                                                                                        </div>
                                                                                </div>
                                                                        </td>
                                                                </tr>
                                                        </template>
                                                </tbody>
                                        </table>
                                </div>

                                <!-- Pagination -->
                                <div v-if="totalPages > 1" class="py-4 px-6 border-t border-gray-200 flex justify-between items-center flex-wrap gap-4">
                                        <p class="text-sm text-gray-500 m-0">
                                                Showing {{ startRow }} to {{ endRow }} of {{ totalRows }} results
                                        </p>
                                        <div class="flex gap-1">
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === 1"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="goToPage(currentPage - 1)"
                                                >
                                                        Previous
                                                </button>
                                                <button
                                                        v-for="page in visiblePages"
                                                        :key="page"
                                                        type="button"
                                                        class="py-2 px-3 border rounded-md text-sm cursor-pointer transition-colors"
                                                        :class="page === currentPage ? 'bg-orange-7 text-white border-orange-7' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
                                                        @click="goToPage(page)"
                                                >
                                                        {{ page }}
                                                </button>
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === totalPages"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="goToPage(currentPage + 1)"
                                                >
                                                        Next
                                                </button>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Create Modal -->
                <div v-if="showCreateModal" class="fixed inset-0 z-50">
                        <div class="fixed inset-0 bg-black/50" @click="showCreateModal = false"></div>
                        <div class="fixed inset-0 overflow-y-auto">
                                <div class="flex min-h-full items-center justify-center p-4">
                                        <div class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-2xl relative z-[51]">
                                                <h3 class="text-xl font-semibold text-gray-900 m-0">
                                                        Create New Offering Option
                                                </h3>
                                                <p class="mt-1 text-sm text-gray-500">
                                                        Add a new offering option.
                                                </p>
                                                <div class="mt-6">
                                                        <label class="block text-sm font-medium text-gray-700 mb-2">
                                                                Name <span class="text-red-600">*</span>
                                                        </label>
                                                        <input
                                                                v-model="newItemName"
                                                                type="text"
                                                                placeholder="Enter offering option name"
                                                                class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
                                                        >
                                                </div>
                                                <div class="mt-6 flex flex-col gap-3">
                                                        <button
                                                                type="button"
                                                                :disabled="isCreating || !newItemName.trim()"
                                                                class="w-full py-2.5 px-4 bg-orange-7 hover:bg-orange-6 text-white rounded-lg text-sm font-medium border-none cursor-pointer transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                                                                @click="createItem"
                                                        >
                                                                {{ isCreating ? 'Creating...' : 'Create Offering Option' }}
                                                        </button>
                                                        <button
                                                                type="button"
                                                                class="w-full py-2.5 px-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                                @click="showCreateModal = false"
                                                        >
                                                                Cancel
                                                        </button>
                                                </div>
                                        </div>
                                </div>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import Swal from 'sweetalert2';
import OfferingOptionService from 'services/OfferingOptionService';
import LanguageService from 'services/LanguageService';
import TranslationService from 'services/TranslationService';
import { httpClient } from 'utils/http';

export default defineComponent({
        name: 'OfferingOptionsPage',
        setup() {
                const router = useRouter();

                const items = ref<any[]>([]);
                const languages = ref<any[]>([]);
                const translations = ref<Record<number, Record<number, string>>>({});
                const originalTranslations = ref<Record<number, Record<number, string>>>({});
                const searchQuery = ref('');
                const translationFilter = ref('all');
                const tagFilter = ref('all');
                const currentPage = ref(1);
                const pageSize = ref(25);
                const isLoading = ref(true);
                const showCreateModal = ref(false);
                const newItemName = ref('');
                const isCreating = ref(false);
                const expandedRowIds = ref(new Set<number>());
                const selectedLanguageId = ref<number>(1);

                const getTranslationStatus = (itemId: number): 'complete' | 'partial' | 'none' => {
                        const itemTranslations = translations.value[itemId];
                        if (!itemTranslations || languages.value.length === 0) return 'none';

                        let completeCount = 0;
                        languages.value.forEach(lang => {
                                const value = itemTranslations[lang.id];
                                if (value && value.trim()) completeCount++;
                        });

                        if (completeCount === languages.value.length) return 'complete';
                        if (completeCount > 0) return 'partial';
                        return 'none';
                };

                const getTranslationStatusClass = (itemId: number): string => {
                        const status = getTranslationStatus(itemId);
                        if (status === 'complete') return 'bg-green-100 text-green-800';
                        if (status === 'partial') return 'bg-orange-100 text-orange-800';
                        return 'bg-red-100 text-red-800';
                };

                const getTranslationStatusLabel = (itemId: number): string => {
                        const status = getTranslationStatus(itemId);
                        if (status === 'complete') return 'Complete';
                        if (status === 'partial') return 'Partial';
                        return 'None';
                };

                const getLanguageTranslationStatus = (itemId: number, langId: number): 'complete' | 'none' => {
                        const itemTranslations = translations.value[itemId];
                        if (!itemTranslations) return 'none';
                        const value = itemTranslations[langId];
                        return value && value.trim() ? 'complete' : 'none';
                };

                const getTranslation = (itemId: number, langId: number): string => {
                        return translations.value[itemId]?.[langId] || '';
                };

                const updateTranslation = (itemId: number, langId: number, value: string) => {
                        if (!translations.value[itemId]) {
                                translations.value[itemId] = {};
                        }
                        translations.value[itemId][langId] = value;
                };

                const hasUnsavedChanges = (itemId: number): boolean => {
                        const current = translations.value[itemId] || {};
                        const original = originalTranslations.value[itemId] || {};
                        
                        for (const lang of languages.value) {
                                if ((current[lang.id] || '') !== (original[lang.id] || '')) {
                                        return true;
                                }
                        }
                        return false;
                };

                const uniqueTags = computed(() => {
                        const tags = new Set<string>();
                        items.value.forEach(item => {
                                if (item.tag && item.tag.trim()) {
                                        tags.add(item.tag);
                                }
                        });
                        return Array.from(tags).sort();
                });

                const filteredItems = computed(() => {
                        let result = items.value;

                        if (searchQuery.value.trim()) {
                                const query = searchQuery.value.toLowerCase();
                                result = result.filter(item =>
                                        item.name?.toLowerCase().includes(query) ||
                                        item.description?.toLowerCase().includes(query) ||
                                        String(item.id).includes(query)
                                );
                        }

                        if (tagFilter.value !== 'all') {
                                if (tagFilter.value === '__empty__') {
                                        result = result.filter(item => !item.tag || !item.tag.trim());
                                } else {
                                        result = result.filter(item => item.tag === tagFilter.value);
                                }
                        }

                        if (translationFilter.value !== 'all') {
                                result = result.filter(item => {
                                        const status = getTranslationStatus(item.id);
                                        return status === translationFilter.value;
                                });
                        }

                        return result;
                });

                const totalRows = computed(() => filteredItems.value.length);
                const totalPages = computed(() => Math.max(1, Math.ceil(totalRows.value / pageSize.value)));
                const startRow = computed(() => totalRows.value === 0 ? 0 : (currentPage.value - 1) * pageSize.value + 1);
                const endRow = computed(() => Math.min(currentPage.value * pageSize.value, totalRows.value));

                const paginatedItems = computed(() => {
                        const start = (currentPage.value - 1) * pageSize.value;
                        return filteredItems.value.slice(start, start + pageSize.value);
                });

                const visiblePages = computed(() => {
                        const pages: number[] = [];
                        const maxVisible = 5;
                        let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
                        const end = Math.min(totalPages.value, start + maxVisible - 1);
                        start = Math.max(1, end - maxVisible + 1);
                        for (let i = start; i <= end; i++) {
                                pages.push(i);
                        }
                        return pages;
                });

                watch([searchQuery, pageSize, translationFilter, tagFilter], () => {
                        currentPage.value = 1;
                });

                const goToPage = (page: number) => {
                        if (page >= 1 && page <= totalPages.value) {
                                currentPage.value = page;
                        }
                };

                const toggleTranslationExpand = (itemId: number) => {
                        if (expandedRowIds.value.has(itemId)) {
                                expandedRowIds.value.delete(itemId);
                        } else {
                                expandedRowIds.value.add(itemId);
                                const defaultLang = languages.value.find(l => l.default);
                                selectedLanguageId.value = defaultLang ? defaultLang.id : languages.value[0]?.id || 1;
                        }
                };

                const loadData = async () => {
                        isLoading.value = true;
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                const [optionData, languageData] = await Promise.all([
                                        OfferingOptionService.getAll(params),
                                        LanguageService.getAll(params),
                                ]);
                                items.value = optionData;
                                languages.value = languageData;

                                const defaultLang = languageData.find((l: any) => l.default);
                                selectedLanguageId.value = defaultLang ? Number(defaultLang.id) : (languageData[0]?.id ? Number(languageData[0].id) : 1);

                                await loadAllTranslations();
                        } catch (error) {
                                console.error('Failed to load data:', error);
                        } finally {
                                isLoading.value = false;
                        }
                };

                const loadAllTranslations = async () => {
                        if (languages.value.length === 0 || items.value.length === 0) return;

                        try {
                                const langIds = languages.value.map(l => l.id).join(' ');
                                const params = new URLSearchParams({
                                        ns: 'offeringoptions',
                                        lng: langIds,
                                        limit: '0',
                                });
                                const data = await TranslationService.getMultiTranslation(params);

                                const newTranslations: Record<number, Record<number, string>> = {};
                                items.value.forEach(item => {
                                        newTranslations[item.id] = {};
                                        languages.value.forEach(lang => {
                                                const langData = data[lang.id]?.offeringoptions as Record<number, string> | undefined;
                                                newTranslations[item.id][lang.id] = langData?.[item.id] || '';
                                        });
                                });
                                translations.value = newTranslations;
                                originalTranslations.value = JSON.parse(JSON.stringify(newTranslations));
                        } catch (error) {
                                console.error('Failed to load translations:', error);
                        }
                };

                const saveTranslations = async (itemId: number) => {
                        try {
                                const itemTranslations = translations.value[itemId];
                                if (!itemTranslations) return;

                                const savePromises = languages.value.map(lang => {
                                        const value = itemTranslations[lang.id] || '';
                                        const originalValue = originalTranslations.value[itemId]?.[lang.id] || '';
                                        if (value !== originalValue) {
                                                return httpClient.put('/api/translation', {
                                                        namespace: 'offeringoptions',
                                                        language: lang.id,
                                                        bundle: { [itemId]: value },
                                                });
                                        }
                                        return Promise.resolve();
                                });

                                await Promise.all(savePromises);
                                originalTranslations.value[itemId] = { ...itemTranslations };

                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'success',
                                        title: 'Translations saved',
                                        showConfirmButton: false,
                                        timer: 1500,
                                        timerProgressBar: true,
                                });
                        } catch (error: any) {
                                console.error('Failed to save translations:', error);
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to save translations',
                                        icon: 'error',
                                });
                        }
                };

                const editItem = (id: number) => {
                        router.push(`/offeringoption/${id}`);
                };

                const confirmDelete = async (item: any) => {
                        const result = await Swal.fire({
                                title: 'Delete Offering Option?',
                                text: `Are you sure you want to delete "${item.name}"? This action cannot be undone.`,
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#dc2626',
                                cancelButtonColor: '#6b7280',
                                confirmButtonText: 'Yes, delete it',
                                cancelButtonText: 'Cancel',
                        });

                        if (result.isConfirmed) {
                                try {
                                        await OfferingOptionService.delete(item.id);
                                        items.value = items.value.filter(i => i.id !== item.id);
                                        Swal.fire({
                                                toast: true,
                                                position: 'top-end',
                                                icon: 'success',
                                                title: 'Offering option deleted',
                                                showConfirmButton: false,
                                                timer: 2000,
                                                timerProgressBar: true,
                                        });
                                } catch (error: any) {
                                        Swal.fire({
                                                title: 'Error',
                                                text: error.message || 'Failed to delete offering option',
                                                icon: 'error',
                                        });
                                }
                        }
                };

                const createItem = async () => {
                        if (!newItemName.value.trim()) return;

                        isCreating.value = true;
                        try {
                                const newItem = await OfferingOptionService.create({ name: newItemName.value.trim() });
                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'success',
                                        title: 'Offering option created',
                                        showConfirmButton: false,
                                        timer: 2000,
                                        timerProgressBar: true,
                                });
                                showCreateModal.value = false;
                                newItemName.value = '';
                                router.push(`/offeringoption/${newItem.id}`);
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to create offering option',
                                        icon: 'error',
                                });
                        } finally {
                                isCreating.value = false;
                        }
                };

                onMounted(() => {
                        loadData();
                });

                return {
                        items,
                        languages,
                        translations,
                        searchQuery,
                        translationFilter,
                        tagFilter,
                        uniqueTags,
                        currentPage,
                        pageSize,
                        isLoading,
                        showCreateModal,
                        newItemName,
                        isCreating,
                        expandedRowIds,
                        selectedLanguageId,
                        filteredItems,
                        totalRows,
                        totalPages,
                        startRow,
                        endRow,
                        paginatedItems,
                        visiblePages,
                        goToPage,
                        toggleTranslationExpand,
                        getTranslationStatus,
                        getTranslationStatusClass,
                        getTranslationStatusLabel,
                        getLanguageTranslationStatus,
                        getTranslation,
                        updateTranslation,
                        hasUnsavedChanges,
                        saveTranslations,
                        editItem,
                        confirmDelete,
                        createItem,
                };
        },
});
</script>
